import metaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import UIControl from "../../../editor/control/ui-control";
import fieldDataSource from "app/components/common/editor/control/select/field-datasource";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import { ITransformationConfiguration } from "../cq";
// Import the embedding generator select field
import EmbeddingGeneratorSelectField from "app/components/common/editor/control/embedding-generator-select-field/embedding-generator-select-field";
import { TransformationUtils } from "./transformation.utils";

const Transformation: ITransformationConfiguration = {
    OnlyDbSources: false,
    allowChangeSource: true
};



Transformation.createColumnControl = function () {
    const self = this;
    // Use the column control for text field selection with aliases
    return UIControl.ColumnsPicker.extend({
        hideLabel: true,
        hidden: false, // Show the column picker
        transformationConfig: {
            preFillColumns: false,
            addAvailable: true, // Allow adding new fields
            autoFillAlias: true,
            hideRemoveIcon: false, // Allow removing fields
            allowClearColumns: true,
            columnsSource: async function (_: any, fieldName: string) {
                const fields = await fieldDataSource(fieldName);

                self.allFields = fields;
                // Filter to only show string and list types
                const supportedFields = fields.filter(field => {
                    const fieldType = field.description?.toLowerCase() || '';
                    // Support only String and List types
                    return fieldType === 'string' ||
                        fieldType.includes('string') ||
                        fieldType.includes('list');
                });

                // Format the fields to show their types
                const formattedFields = supportedFields.map(field => ({
                    id: field.id,
                    text: field.text,
                    description: field.description,
                }));

                return formattedFields;
            },
            labels: {
                column1: "Text Field",
                column2: "Alias"
            },
            addLabel: "ADD FIELD" // Custom label for the add button
        }
    }).create();
};

const getEuclidTqlBuilder = (stream: string, columns: any[], options: any, transformation: any) => ({
    build() {
        if (!stream) {
            return "";
        }

        // Check if we have any columns
        if (!columns || columns.length === 0) {
            return "Please select at least one Text Field";
        }

        // Filter out any empty column entries
        const validColumns = columns.filter(col => col && col.column && col.column.trim() !== '');
        // Get the selected fields from the field selection picker
        const selectedFields = options.StaticControls.fieldsToBePassedOn.getViewValue() || [];
        const validSelectedFields = selectedFields.filter(field => field && field.column && field.column.trim() !== '');

        if (validColumns.length === 0) {
            return "Please select at least one Text Field";
        }

        // Checking Columns for Combination of individual and collection datatype being configured. DEV-52441

        let hasIndividual = false;
        let hasCollection = false;

        for (let i = 0; i < validColumns.length; i++) {
            const validColumn = validColumns[i];
            if (!validColumn.column) {
                continue;
            }

            const description = validColumn.columns.filter(col => col.id === validColumn.column)[0]?.description;
            if (description && description.toLowerCase().includes("list")) {
                hasCollection = true;
            } else {
                hasIndividual = true;
            }
        }
        if (hasIndividual && hasCollection) {
            return "-- Please select either individual or collection fields, not both";
        }


        // Get the embedding generator name
        const embeddingGeneratorFullName = options.StaticControls.embeddingGenerator.getValue();
        if (!embeddingGeneratorFullName) {
            return "Please select an Embedding Generator from the dropdown";
        }

        // Parse the embedding generator name to get namespace and name
        const embeddingGeneratorId = metaObjectConverter.parse(embeddingGeneratorFullName);
        const embeddingGeneratorNs = embeddingGeneratorId.nsName;
        const embeddingGeneratorName = embeddingGeneratorId.name;

        // Save all values to metadata_object.uiconfig.config
        if (transformation && transformation.metadata_object && transformation.metadata_object.uiconfig) {
            transformation.metadata_object.uiconfig.config = transformation.metadata_object.uiconfig.config || {};
            transformation.metadata_object.uiconfig.config.columns = validColumns;
            transformation.metadata_object.uiconfig.config.embeddingGenerator = embeddingGeneratorFullName;
            transformation.metadata_object.uiconfig.config.fieldsToBePassedOn = validSelectedFields;
        }

        // Build the TQL
        let tql = "";

        // Add the generateEmbeddings function call for each selected text field
        if (hasIndividual) {
            // If specific fields are selected, use them instead of wildcard
            if (validSelectedFields.length > 0) {
                tql = "SELECT " + validSelectedFields.map(field => {
                    const fieldName = field.column;
                    // Use alias for field selection if provided (for string fields)
                    return field.alias ? `${fieldName} AS ${field.alias}` : fieldName;
                }).join(", ");
                // Add comma if we had added selected fields
                if (validColumns.length > 0) {
                    tql += ", ";
                }
            } else {
                tql = "SELECT ";
            }

            // Add the embedding function calls
            for (let i = 0; i < validColumns.length; i++) {
                if (i > 0) {
                    tql += ", ";
                }
                const column = validColumns[i];
                const fieldName = column.column;
                const alias = column.alias || `${fieldName}_embeddings`;
                tql += `generateEmbeddings("${embeddingGeneratorNs}.${embeddingGeneratorName}", ${fieldName}) AS ${alias}`;
            }
            tql += `\nFROM ${stream}`;
        }
        if (hasCollection) {
            // For collection types, determine if we need makeTupleList
            const needsMakeTupleList = validSelectedFields.length > 0 || validColumns.length > 1;

            if (needsMakeTupleList) {
                // Start the SELECT statement
                tql = "SELECT makeTupleList(";

                // Add selected fields if any
                if (validSelectedFields.length > 0) {
                    tql += validSelectedFields.map(field => field.column).join(", ");
                    // Add comma if we're going to add embedding columns
                    if (validColumns.length > 0) {
                        tql += ", ";
                    }
                }

                // Add embedding columns
                for (let i = 0; i < validColumns.length; i++) {
                    if (i > 0) {
                        tql += ", ";
                    }
                    const column = validColumns[i];
                    const fieldName = column.column;
                    tql += `generateEmbeddingsPerBatch("${embeddingGeneratorNs}.${embeddingGeneratorName}", ${fieldName})`;
                }

                tql += `) as VectorTuple \nFROM ${stream}`;
            } else {
                // Simple case: just one column
                const column = validColumns[0];
                const fieldName = column.column;
                tql = `SELECT generateEmbeddingsPerBatch("${embeddingGeneratorNs}.${embeddingGeneratorName}", ${fieldName}) as VectorTuple \nFROM ${stream}`;

            }
        }
        return tql;
    }
});

Transformation.createRqlBuilder = getEuclidTqlBuilder;

function getControlRow(control) {
    return control.$el.closest(".field-row");
}

Transformation.convertToCQ = function () {
    // Hide all controls when converting to CQ
    if (this.options.StaticControls.embeddingGenerator) {
        getControlRow(this.options.StaticControls.embeddingGenerator).hide();
    }

    if (this.options.StaticControls.fieldsToBePassedOn) {
        getControlRow(this.options.StaticControls.fieldsToBePassedOn).hide();
    }

    // Hide the columns control
    if (this.columns && this.columns.$el) {
        this.columns.$el.closest('.field-row').hide();
    }
};

Transformation.buildTql = function () { };

Transformation.postProcessing = function (cqControl: any, options: any, metadata_object: any) {
    // Store original values to track changes
    let originalColumns = [];
    let originalfieldsToBePassedOn = [];
    let originalEmbeddingGenerator = "";
    options.labels.input = {
        name: "Input",
        description:
            "Only fields of type String or List<String> can be selected for embedding generation in Euclid"
    }
    if (metadata_object.uiconfig && metadata_object.uiconfig.config) {

        if (metadata_object.uiconfig.config.columns) {
            // Normalize columns by filtering out empty entries
            originalColumns = metadata_object.uiconfig.config.columns.filter(
                col => col && col.column && col.column.trim() !== ''
            );
        }
        if (metadata_object.uiconfig.config.fieldsToBePassedOn) {
            // Normalize field selection by filtering out empty entries
            originalfieldsToBePassedOn = metadata_object.uiconfig.config.fieldsToBePassedOn.filter(
                field => field && field.column && field.column.trim() !== ''
            );
        }
        if (metadata_object.uiconfig.config.embeddingGenerator) {
            originalEmbeddingGenerator = metadata_object.uiconfig.config.embeddingGenerator;
        }
    }

    const originalValues = {
        columns: JSON.stringify(originalColumns),
        fieldsToBePassedOn: JSON.stringify(originalfieldsToBePassedOn), // Initialize with empty array
        embeddingGenerator: originalEmbeddingGenerator
    };

    // Flag to track if this is a new component or an existing one
    const isNewComponent = !metadata_object.uiconfig || !metadata_object.uiconfig.config ||
        (!metadata_object.uiconfig.config.columns && !metadata_object.uiconfig.config.embeddingGenerator && !metadata_object.uiconfig.config.fieldsToBePassedOn);

    // Initialize form's dirty state
    const updateFormDirtyState = (isDirty) => {
        if (cqControl.Form && cqControl.Form.model) {
            cqControl.Form.model.isDirty = isDirty;
            if (!isDirty) {
                cqControl.Form.model.dirtyControls = [];
            }
            cqControl.Form.model.refreshDirty();

            // Update the ActionsView UI state
            if (cqControl.Form.view && cqControl.Form.view.actionsView) {
                cqControl.Form.view.actionsView.onDirtyChange();

                // For existing components, disable the save button if not dirty
                if (!isNewComponent && !isDirty) {
                    cqControl.Form.view.actionsView.disabled = true;
                    cqControl.Form.view.actionsView.ui.btnOk.addClass("disabled");
                } else {
                    cqControl.Form.view.actionsView.disabled = false;
                    cqControl.Form.view.actionsView.ui.btnOk.removeClass("disabled");
                }
            }
        }
    };

    // Check if current values differ from original values
    const checkForChanges = () => {
        if (isNewComponent) {
            // For new components, always enable the save button
            updateFormDirtyState(true);
            return;
        }

        // Get current values
        const currentColumns = cqControl.columns.getViewValue() || [];
        const currentEmbeddingGenerator = options.StaticControls.embeddingGenerator.getValue() || "";
        const currentfieldsToBePassedOn = options.StaticControls.fieldsToBePassedOn.getViewValue() || [];


        // Normalize columns for comparison by filtering out empty entries
        const normalizedCurrentColumns = currentColumns.filter(col => col && col.column && col.column.trim() !== '');
        const normalizedCurrentfieldsToBePassedOn = currentfieldsToBePassedOn.filter(field => field && field.column && field.column.trim() !== '');

        // Convert to strings for comparison
        const currentColumnsStr = JSON.stringify(normalizedCurrentColumns);
        const currentfieldsToBePassedOnStr = JSON.stringify(normalizedCurrentfieldsToBePassedOn);

        const hasChanges =
            (currentColumnsStr !== originalValues.columns) ||
            (currentfieldsToBePassedOnStr !== (originalValues.fieldsToBePassedOn || '[]')) ||
            (currentEmbeddingGenerator !== originalValues.embeddingGenerator);


        // Update form dirty state based on whether there are changes
        updateFormDirtyState(hasChanges);

        // Explicitly update the save button state
        if (cqControl.Form && cqControl.Form.view && cqControl.Form.view.actionsView) {
            if (hasChanges) {
                cqControl.Form.view.actionsView.disabled = false;
                cqControl.Form.view.actionsView.ui.btnOk.removeClass("disabled");
            } else {
                cqControl.Form.view.actionsView.disabled = true;
                cqControl.Form.view.actionsView.ui.btnOk.addClass("disabled");
            }
        }
    };

    // Initialize form state
    if (cqControl.Form && cqControl.Form.model) {
        // Set initial dirty state - for existing components, start with save button disabled
        updateFormDirtyState(isNewComponent);

        // Listen for the form's render event to ensure the Save button state is correct
        cqControl.Form.on("form:rendered", function () {
            setTimeout(function () {
                // For existing components, explicitly disable the save button initially
                if (!isNewComponent) {
                    if (cqControl.Form.view && cqControl.Form.view.actionsView) {
                        cqControl.Form.view.actionsView.disabled = true;
                        cqControl.Form.view.actionsView.ui.btnOk.addClass("disabled");
                    }
                }
                updateFormDirtyState(isNewComponent);
            }, 100);
        });

        // Also listen for the form's show event
        cqControl.Form.on("form:show", function () {
            setTimeout(function () {
                // For existing components, explicitly disable the save button
                if (!isNewComponent) {
                    if (cqControl.Form.view && cqControl.Form.view.actionsView) {
                        cqControl.Form.view.actionsView.disabled = true;
                        cqControl.Form.view.actionsView.ui.btnOk.addClass("disabled");
                    }
                }
            }, 100);
        });
    }

    // Creating Field Selection Control to let user decide which fields will go downstream
    options.StaticControls.fieldsToBePassedOn = UIControl.ColumnsPicker.extend({
        name: "fieldsToBePassedOn",
        hideLabel: false,
        label: "Fields To Be Passed On",
        hidden: false, // Initially hide the column picker until input stream is configured
        transformationConfig: {
            preFillColumns: false,
            addAvailable: true, // Allow adding new fields
            autoFillAlias: false, // Disable auto-filling alias
            hideRemoveIcon: false, // Allow removing fields
            allowClearColumns: true,
            hideAliasColumn: false, // Keep alias column visible for both string and list types
            columnsSource: async function (_: any, fieldName: string) {
                const fields = await fieldDataSource(fieldName);

                // Check if the first picker has any list types selected
                const columnsFromFirstPicker = cqControl.columns.getViewValue() || [];
                const validColumns = columnsFromFirstPicker.filter(col => col && col.column && col.column.trim() !== '');

                let hasListType = false;
                for (let i = 0; i < validColumns.length; i++) {
                    const validColumn = validColumns[i];
                    if (!validColumn.column) continue;

                    const description = validColumn.columns.filter(col => col.id === validColumn.column)[0]?.description;
                    if (description && description.toLowerCase().includes("list")) {
                        hasListType = true;
                        break;
                    }
                }

                // Filter fields based on whether list types were selected in first picker
                const filteredFields = fields.filter(field => {
                    const fieldType = field.description?.toLowerCase() || '';
                    if (hasListType) {
                        // If list types were selected, only show list types
                        return fieldType.includes('list');
                    } else {
                        // Otherwise show all fields
                        return true;
                    }
                });

                // Format the fields to show their types
                const formattedFields = filteredFields.map(field => ({
                    id: field.id,
                    text: field.text,
                    description: field.description,
                    // Add any additional properties needed
                }));

                return formattedFields;
            },
            labels: {
                column1: "Field",
                column2: "Alias"
            },
            addLabel: "ADD FIELD" // Custom label for the add button
        },
        onchange: (value: any) => {
            // Save the selected fields to metadata_object.uiconfig.config
            metadata_object.uiconfig.config = metadata_object.uiconfig.config || {};
            metadata_object.uiconfig.config.fieldsToBePassedOn = value;
            cqControl.refreshTql();
            // Check for changes to update the form's dirty state
            checkForChanges();
        }
    }).create();

    // Create the embedding generator control using the new selector component
    options.StaticControls.embeddingGenerator = EmbeddingGeneratorSelectField({
        name: "embeddingGenerator",
        label: "Embedding Generator",
        required: true,
        enabled: true,
        onChange: (selectedEmbeddingGenerator: string) => {
            metadata_object.uiconfig.config = metadata_object.uiconfig.config || {};
            metadata_object.uiconfig.config.embeddingGenerator = selectedEmbeddingGenerator;
            cqControl.refreshTql();
            // Check for changes to update the form's dirty state
            checkForChanges();
        }
    }).create();


    // Reorder the form fields to match the desired layout:
    // 1. Input stream (already at the top)
    // 2. Text field section (columns control)
    // 3. Embedding generator
    // 4. Field Selection
    // 5. Convert to CQ option
    // 6. Query section

    // First, let's find the indices of all relevant fields
    const columnsIndex = options.displayFields.indexOf("columns");
    let selectIndex = options.displayFields.indexOf("select"); // Query box
    const convertToCQIndex = options.displayFields.indexOf("convertToCQ");

    // Remove fields from their current positions
    if (convertToCQIndex !== -1) {
        options.displayFields.splice(convertToCQIndex, 1);
    }


    // Now position the controls in the correct order
    // First, add the columns control (should already be in displayFields)
    // Then add embedding generator
    if (columnsIndex !== -1) {
        options.displayFields.splice(columnsIndex + 1, 0, "embeddingGenerator", "fieldsToBePassedOn");
    } else {
        // If columns control is not found, add it before the query box
        options.displayFields.splice(selectIndex, 0, "embeddingGenerator", "fieldsToBePassedOn");
    }

    // // Then add field selection after embedding generator
    // const embeddingGeneratorIndex = options.displayFields.indexOf("embeddingGenerator");
    // if (embeddingGeneratorIndex !== -1) {
    //     options.displayFields.splice(embeddingGeneratorIndex + 1, 0, "fieldsToBePassedOn");
    // } else {
    //     // If embedding generator is not found, add it before the query box
    //     options.displayFields.splice(selectIndex, 0, "fieldsToBePassedOn");
    // }

    selectIndex = options.displayFields.indexOf("select");
    // Finally add convertToCQ right before the query box
    options.displayFields.splice(selectIndex, 0, "convertToCQ");

    cqControl.input.on(UIControl.EVENT.VALUE_CHANGED, function (inputValue) {
        if (!inputValue) {
            // Hide the field selection picker when no input stream is selected
            getControlRow(options.StaticControls.fieldsToBePassedOn).hide();
            return;
        }

        // Mark form as dirty when input changes
        if (cqControl.Form && cqControl.Form.model) {
            cqControl.Form.model.isDirty = true;
            cqControl.Form.model.refreshDirty();
        }

        // Load fields for the selected stream
        metaStoreService.getStreamFields(inputValue).then(function (fields) {
            // Set the stream for the columns picker to load fields
            cqControl.columns.setStream(inputValue);

            // Also set the stream for the field selection picker
            options.StaticControls.fieldsToBePassedOn.setStream(inputValue);

            // Show the field selection picker now that we have an input stream
            getControlRow(options.StaticControls.fieldsToBePassedOn).show();

            // Add a listener for when a new column is added in the first picker
            if (!cqControl.columns._newColumnAddedListenerAttached) {
                cqControl.columns._newColumnAddedListenerAttached = true;

                // Listen for the "newColumnAdded" event
                cqControl.columns.on("newColumnAdded", () => {
                    setTimeout(() => {
                        cqControl.refreshTql();
                    }, 100);
                });
            }

            // Add a listener for when a new field is added in the field selection picker
            if (!options.StaticControls.fieldsToBePassedOn._newColumnAddedListenerAttached) {
                options.StaticControls.fieldsToBePassedOn._newColumnAddedListenerAttached = true;

                // Listen for the "newColumnAdded" event
                options.StaticControls.fieldsToBePassedOn.on("newColumnAdded", () => {
                    setTimeout(() => {
                        cqControl.refreshTql();
                    }, 100);
                });
            }

            // If we have a saved embedding generator, restore it
            if (metadata_object.uiconfig && metadata_object.uiconfig.config &&
                metadata_object.uiconfig.config.embeddingGenerator) {
                options.StaticControls.embeddingGenerator.setValue(metadata_object.uiconfig.config.embeddingGenerator);
            }

            // If we have saved field selection values, restore them
            if (metadata_object.uiconfig && metadata_object.uiconfig.config &&
                metadata_object.uiconfig.config.fieldsToBePassedOn) {
                options.StaticControls.fieldsToBePassedOn.setValue(metadata_object.uiconfig.config.fieldsToBePassedOn);
                options.StaticControls.fieldsToBePassedOn.setStream(inputValue);
            }

            // Refresh the TQL
            setTimeout(() => {
                cqControl.refreshTql();
            }, 300);
        });
    });

    // Set up event handlers for control value changes
    // Listen for changes in the columns picker
    cqControl.columns.on("value:set", () => {
        metadata_object.uiconfig.config = metadata_object.uiconfig.config || {};
        metadata_object.uiconfig.config.columns = cqControl.columns.getViewValue();

        // Refresh TQL with a slight delay to ensure the columns are properly updated
        setTimeout(() => {
            cqControl.refreshTql();
        }, 50);

        // Check for changes to update the form's dirty state
        checkForChanges();
    });

    // Also listen for the modified event from the columns grid
    cqControl.columns.gridView.on("modified", () => {
        metadata_object.uiconfig.config = metadata_object.uiconfig.config || {};
        metadata_object.uiconfig.config.columns = cqControl.columns.getViewValue();

        // Refresh TQL with a slight delay
        setTimeout(() => {
            cqControl.refreshTql();
        }, 50);

        // Check for changes to update the form's dirty state
        checkForChanges();
    });

    // Add a direct event listener for the "Add Field" button click
    setTimeout(() => {
        if (cqControl.columns && cqControl.columns.$el) {
            const addButton = cqControl.columns.$el.find('.add');
            if (addButton.length > 0) {
                addButton.on('click', () => {
                    setTimeout(() => {
                        cqControl.refreshTql();
                    }, 200);
                });
            }
        }
    }, 500);

    // Event handler for embedding generator is now set in the control creation

    // Set up event handlers for the field selection control
    options.StaticControls.fieldsToBePassedOn.on("value:set", () => {
        metadata_object.uiconfig.config = metadata_object.uiconfig.config || {};
        metadata_object.uiconfig.config.fieldsToBePassedOn = options.StaticControls.fieldsToBePassedOn.getViewValue();

        // Refresh TQL with a slight delay
        setTimeout(() => {
            cqControl.refreshTql();
        }, 50);

        // Check for changes to update the form's dirty state
        checkForChanges();
    });

    // Also listen for the modified event from the field selection grid
    options.StaticControls.fieldsToBePassedOn.gridView.on("modified", () => {
        metadata_object.uiconfig.config = metadata_object.uiconfig.config || {};
        metadata_object.uiconfig.config.fieldsToBePassedOn = options.StaticControls.fieldsToBePassedOn.getViewValue();

        // Refresh TQL with a slight delay
        setTimeout(() => {
            cqControl.refreshTql();
        }, 50);

        // Check for changes to update the form's dirty state
        checkForChanges();
    });

    // Listen for changes in the first column picker to update field selection options
    cqControl.columns.on("value:set", () => {
        // When the first picker changes, we may need to update the field selection options
        // based on whether list types are selected
        if (cqControl.input.getValue()) {
            options.StaticControls.fieldsToBePassedOn.setStream(cqControl.input.getValue());
        }

        metadata_object.uiconfig.config = metadata_object.uiconfig.config || {};
        metadata_object.uiconfig.config.columns = cqControl.columns.getViewValue();

        // Check if we have list types selected
        const columnsFromFirstPicker = cqControl.columns.getViewValue() || [];
        const validColumns = columnsFromFirstPicker.filter(col => col && col.column && col.column.trim() !== '');

        let hasListType = false;
        for (let i = 0; i < validColumns.length; i++) {
            const validColumn = validColumns[i];
            if (!validColumn.column) continue;

            const description = validColumn.columns.filter(col => col.id === validColumn.column)[0]?.description;
            if (description && description.toLowerCase().includes("list")) {
                hasListType = true;
                break;
            }
        }


        // Refresh TQL with a slight delay to ensure the columns are properly updated
        setTimeout(() => {
            cqControl.refreshTql();
        }, 50);

        // Check for changes to update the form's dirty state
        checkForChanges();
    });

    // Override refreshTql to ensure the query field is updated
    const originalRefreshTql = cqControl.refreshTql;
    cqControl.refreshTql = function () {
        // Call the original refreshTql method
        originalRefreshTql.call(cqControl);

        // Get the columns from the column picker
        const columns = cqControl.columns.getViewValue();

        // Get the selected fields from the field selection picker
        const selectedFields = options.StaticControls.fieldsToBePassedOn.getViewValue() || [];
        const validSelectedFields = selectedFields.filter(field => field && field.column && field.column.trim() !== '');

        const embeddingGeneratorFullName = options.StaticControls.embeddingGenerator.getValue();

        // Get the selected stream
        const selectedStreamId = cqControl.input.getValue();

        if (!selectedStreamId) {
            cqControl.fieldMapper.select.setValue("-- Please select an input stream");
            return;
        }

        const stream = TransformationUtils.getInputStreamName(selectedStreamId);

        // Check if we have any text fields selected
        // First, ensure columns is an array
        const columnsArray = Array.isArray(columns) ? columns : [];

        // Filter out any empty column entries
        const validColumns = columnsArray.filter(col => col && col.column && col.column.trim() !== '');

        if (validColumns.length === 0) {
            // Set a message in the TQL field
            cqControl.fieldMapper.select.setValue("-- Please select at least one Text Field");
            return;
        }

        // Checking Columns for Combination of individual and collection datatype being configured. DEV-52441

        let hasIndividual = false;
        let hasCollection = false;

        for (let i = 0; i < validColumns.length; i++) {
            const validColumn = validColumns[i];
            if (!validColumn.column) {
                continue;
            }

            const description = validColumn.columns.filter(col => col.id === validColumn.column)[0]?.description;
            if (description && description.toLowerCase().includes("list")) {
                hasCollection = true;
            } else {
                hasIndividual = true;
            }
        }

        if (hasIndividual && hasCollection) {
            cqControl.fieldMapper.select.setValue("-- Please select either individual or collection fields, not both");
            return;
        }

        if (!embeddingGeneratorFullName) {
            // Set a message in the TQL field
            cqControl.fieldMapper.select.setValue("-- Please select an Embedding Generator");
            return;
        }

        // Parse the embedding generator name to get namespace and name
        const embeddingGeneratorId = metaObjectConverter.parse(embeddingGeneratorFullName);
        const embeddingGeneratorNs = embeddingGeneratorId.nsName;
        const embeddingGeneratorName = embeddingGeneratorId.name;

        // Save all values to metadata_object.uiconfig.config
        metadata_object.uiconfig.config = metadata_object.uiconfig.config || {};
        metadata_object.uiconfig.config.columns = validColumns;
        metadata_object.uiconfig.config.fieldsToBePassedOn = validSelectedFields;
        metadata_object.uiconfig.config.embeddingGenerator = embeddingGeneratorFullName;

        // Build the TQL
        let tql = "";

        // Add the generateEmbeddings function call for each selected text field
        if (hasIndividual) {
            // If specific fields are selected, use them instead of wildcard
            if (validSelectedFields.length > 0) {
                tql = "SELECT " + validSelectedFields.map(field => {
                    const fieldName = field.column;
                    // Use alias for field selection if provided (for string fields)
                    return field.alias ? `${fieldName} AS ${field.alias}` : fieldName;
                }).join(", ");
                // Add comma if we had added selected fields
                if (validColumns.length > 0) {
                    tql += ", ";
                }
            } else {
                tql = "SELECT ";
            }

            // Add the embedding function calls
            for (let i = 0; i < validColumns.length; i++) {
                if (i > 0) {
                    tql += ", ";
                }
                const column = validColumns[i];
                const fieldName = column.column;
                const alias = column.alias || `${fieldName}_embeddings`;
                tql += `generateEmbeddings("${embeddingGeneratorNs}.${embeddingGeneratorName}", ${fieldName}) AS ${alias}`;
            }
            tql += `\nFROM ${stream}`;
        }
        if (hasCollection) {
            // For collection types, determine if we need makeTupleList
            const needsMakeTupleList = validSelectedFields.length > 0 || validColumns.length > 1;

            if (needsMakeTupleList) {
                // Start the SELECT statement
                tql = "SELECT makeTupleList(";

                // Add selected fields if any
                if (validSelectedFields.length > 0) {
                    tql += validSelectedFields.map(field => field.column).join(", ");
                    // Add comma if we're going to add embedding columns
                    if (validColumns.length > 0) {
                        tql += ", ";
                    }
                }

                // Add embedding columns
                for (let i = 0; i < validColumns.length; i++) {
                    if (i > 0) {
                        tql += ", ";
                    }
                    const column = validColumns[i];
                    const fieldName = column.column;
                    tql += `generateEmbeddingsPerBatch("${embeddingGeneratorNs}.${embeddingGeneratorName}", ${fieldName})`;
                }

                tql += `) as VectorTuple \nFROM ${stream}`;
            } else {
                // Simple case: just one column
                const column = validColumns[0];
                const fieldName = column.column;
                tql = `SELECT generateEmbeddingsPerBatch("${embeddingGeneratorNs}.${embeddingGeneratorName}", ${fieldName}) as VectorTuple \nFROM ${stream}`;

            }
        }

        // Store the query in the metadata
        metadata_object.uiconfig.query = tql;

        // Directly set the query field
        cqControl.fieldMapper.select.setValue(tql);
    };

    // Initialize the form with saved values if available
    if (metadata_object.uiconfig && metadata_object.uiconfig.config) {
        if (metadata_object.uiconfig.config.embeddingGenerator) {
            options.StaticControls.embeddingGenerator.setValue(metadata_object.uiconfig.config.embeddingGenerator);
        }

        // Restore saved field selection if available
        if (metadata_object.uiconfig.config.fieldsToBePassedOn && metadata_object.uiconfig.config.fieldsToBePassedOn.length > 0) {
            options.StaticControls.fieldsToBePassedOn.setValue(metadata_object.uiconfig.config.fieldsToBePassedOn);
        }

        // Make sure the field selection picker is visible
        if (cqControl.input.getValue()) {
            getControlRow(options.StaticControls.fieldsToBePassedOn).show();
            options.StaticControls.fieldsToBePassedOn.setStream(cqControl.input.getValue());
        }
    }

    // Add a listener to ensure the field selection picker is shown when the form is rendered
    cqControl.Form.on("form:render", function () {
        setTimeout(function () {
            if (cqControl.input.getValue()) {
                getControlRow(options.StaticControls.fieldsToBePassedOn).show();
            }
        }, 100);
    });

    // Listen for form submission to reset dirty state after save
    if (cqControl.Form && cqControl.Form.view) {
        cqControl.Form.view.on("submit", function () {
            // Reset dirty state after successful save
            setTimeout(function () {
                // Get current values
                const currentColumns = cqControl.columns.getViewValue() || [];

                // Normalize columns for comparison by filtering out empty entries
                const normalizedCurrentColumns = currentColumns.filter(col => col && col.column && col.column.trim() !== '');

                // Update the original values to the current values
                originalValues.columns = JSON.stringify(normalizedCurrentColumns);
                originalValues.embeddingGenerator = options.StaticControls.embeddingGenerator.getValue() || "";

                // Update form state
                updateFormDirtyState(false);

                // Explicitly disable the save button
                if (cqControl.Form.view.actionsView) {
                    cqControl.Form.view.actionsView.disabled = true;
                    cqControl.Form.view.actionsView.ui.btnOk.addClass("disabled");
                }
            }, 100);
        });
    }
};

export default Transformation;
